import { cn } from '@/utils/utils';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useResetConferenceVirtualSettingsByOrganization } from '@/features/conferences/api/conferenceVirtualSettings';
import OncolensToast from '@/components/OncolensToast';
import { useAddOrganizationLicenses } from '../api/addOrganizationLicenses';
import { useDeleteOrganizationLicense } from '../api/deleteOrganizationLicense';
import { useGetAvailableLicenses } from '../api/getAvailableLicenses';
import { useGetOrganizationLicenses } from '../api/getOrganizationLicenses';
import { OrganizationKeys } from '../api/organization.keys';
import { Licenses } from '../types';

export function LicensesForm({ organizationId }: { organizationId: string }) {
  const { data: serverLicenses = [], isLoading: areOrgLicensesLoading } =
    useGetOrganizationLicenses(organizationId);
  const { data: availableLicenses, isLoading: areAvailableLoading } = useGetAvailableLicenses();
  const resetConferenceVirtualSettings = useResetConferenceVirtualSettingsByOrganization();

  if (areOrgLicensesLoading || areAvailableLoading) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col space-y-3">
        {availableLicenses
          ?.slice()
          .sort((a, b) => a.localeCompare(b))
          .map((name) => (
            <Option
              key={name}
              serverLicenses={serverLicenses}
              name={name}
              organizationId={organizationId}
              resetConferenceVirtualSettings={resetConferenceVirtualSettings}
            />
          ))}
      </div>
    </div>
  );
}

function Option({
  serverLicenses,
  name,
  organizationId,
  resetConferenceVirtualSettings,
}: {
  serverLicenses: { id: string; license: string }[];
  name: string;
  organizationId: string;
  resetConferenceVirtualSettings: ReturnType<
    typeof useResetConferenceVirtualSettingsByOrganization
  >;
}) {
  const queryClient = useQueryClient();
  const tickVariants = {
    checked: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: 0.2, delay: 0.1 },
    },
    unchecked: {
      pathLength: 0,
      opacity: 0,
      transition: { duration: 0.2 },
    },
  };

  const addApi = useAddOrganizationLicenses();
  const deleteApi = useDeleteOrganizationLicense();
  const isSelected = (name: string) => serverLicenses.some((l) => l.license === name);
  const selected = isSelected(name);

  const handleLicenseToggle = (name: string) => {
    const record = serverLicenses.find((l) => l.license === name);
    if (!isSelected(name)) {
      addApi.mutate({ organizationId, licenses: [name] });
    } else {
      deleteApi.mutate(
        {
          licenseId: record?.id ?? '',
        },
        {
          onSuccess: () => {
            // Reset conference virtual settings when Virtual Conference license is removed
            if (name === Licenses.VirtualConference) {
              resetConferenceVirtualSettings.mutate(organizationId, {
                onSuccess: () => {
                  queryClient.invalidateQueries(OrganizationKeys.detail(organizationId));
                  queryClient.invalidateQueries(
                    OrganizationKeys.license(organizationId, Licenses.VirtualConference),
                  );
                },
                onError: () => {
                  OncolensToast.error(
                    'License removed, but conference-level virtual settings overrides could not be reset.',
                  );
                },
              });
            }
          },
        },
      );
    }
  };

  return (
    <AnimatePresence key={name}>
      {/* the option */}
      <motion.button
        className={cn(
          'relative flex items-center px-6 py-3 rounded-lg border-2 cursor-pointer w-full max-w-xs shadow-md ',
          selected
            ? 'bg-oncolensBlue-500 text-white border-oncolensBlue-500 hover:bg-oncolensBlue-500/90'
            : 'bg-white border-oncolensOrange-500 hover:bg-slate-50',
        )}
        onClick={() => handleLicenseToggle(name)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}>
        {/* The option  icon */}
        <div className="flex items-center w-full">
          <motion.div
            className={cn(
              'w-5 h-5 rounded-full border-2 flex items-center justify-center',
              selected ? 'border-white' : 'border-gray-400',
            )}
            animate={{ borderColor: selected ? '#fff' : '#9ca3af' }}
            transition={{ duration: 0.2 }}>
            <AnimatePresence>
              {selected && (
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth={3.5}
                  stroke="currentColor"
                  fill="none"
                  className="w-3 h-3"
                  initial="unchecked"
                  animate="checked"
                  exit="unchecked">
                  <motion.path
                    d="M4.5 12.75l6 6 9-13.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    variants={tickVariants}
                  />
                </motion.svg>
              )}
            </AnimatePresence>
          </motion.div>
          <span className="ml-3 text-lg">{name}</span>
        </div>
      </motion.button>
    </AnimatePresence>
  );
}
